import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import Helmet from 'react-helmet';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from '@emotion/styled';
import { format } from 'date-fns';

import Layout from '../../shared/components/Layout/Layout';
import Button from '../../shared/_TBComponents/Button';
import HeaderText from '../../shared/_TBComponents/HeaderText';
import Text from '../../shared/_TBComponents/Text';
import MultiCategoryFilter from './components/presentational/MultiCategoryFilter';
import ExercisePicker from './components/presentational/ExercisePicker';
import GoalsCard from './components/presentational/GoalsCard';
import ManageMenu from './components/presentational/ManageMenu';
import MaxesTable from './components/presentational/tables/MaxesTable';
import AddMaxesModal from './modals/AddMaxesModal';
import ConfirmActionModal from './modals/ConfirmActionModal';
import ExportMaxesModal from './modals/ExportMaxesModal';
import SetGoalModal from './modals/SetGoalModal';
import USER_FILTER_TYPE, { EXERCISE_TYPE_ICON } from './constants';
import {
  getMaxEntryFieldConfig, resolveWeightUnitAbbr, getGoalSourceLabel, getMaxSourceLabel,
} from './utils/maxesGoalsHelpers';
import {
  fetchAccountMaxes, fetchAccountGoals, fetchGroups, fetchCalendars, fetchAthletes,
  fetchExercises, setMaxesGoalsFilter, createMaxEfforts, saveGoal, deleteGoals,
  fetchMyMaxes, fetchMyGoals, createMyMaxEffort,
} from './ducks/maxesGoalsActions';
import useManageMaxes from './hooks/useManageMaxes';

// Groups and calendars show one exercise so wait until one is picked
const shouldFetchMaxes = (filter) => {
  if (!filter.filterType) return false;
  if (filter.filterType === USER_FILTER_TYPE.Athletes) return true;
  return Boolean(filter.exerciseId);
};

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100% - 49px);
  margin-top: 49px;
  padding-bottom: 16px;
  overflow: scroll;
  background: #F5F5F5;
`;

const HeaderWrapper = styled.div`
  padding: 16px 16px 0 16px;
`;

const TopRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 16px;
  margin: 16px 16px 0 16px;
`;

const FiltersCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 320px;
  max-width: 440px;
  min-height: 220px;
  padding: 16px;
  background: #FFFFFF;
  border: 1px solid #D6D6D6;
  border-radius: 4px;
  box-shadow: 0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05);
`;

const TableHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 16px 16px 0 16px;
`;

const TableHeaderActions = styled.div`
  display: flex;
  gap: 8px;
`;

const HeaderButton = styled.button`
  border: 1px solid #D6D6D6;
  border-radius: 4px;
  background: #FFFFFF;
  padding: 8px 16px;
  font-family: "Nunito Sans";
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: #F7F7F7;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const MaxesGoals = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const { accountCode } = currentUser;
  const orgWeight = useSelector((state) => state.auth.data.currentUser?.userSettings?.orgWeight);
  const accountMaxes = useSelector((state) => state.maxesGoals.data.accountMaxes);
  const accountGoals = useSelector((state) => state.maxesGoals.data.accountGoals);
  const groups = useSelector((state) => state.maxesGoals.data.groups);
  const calendars = useSelector((state) => state.maxesGoals.data.calendars);
  const athletes = useSelector((state) => state.maxesGoals.data.athletes);
  const exercises = useSelector((state) => state.maxesGoals.data.exercises);
  const storedFilter = useSelector((state) => state.maxesGoals.ui.activeFilter);
  const isAccountMaxesLoading = useSelector((state) => state.maxesGoals.ui.isAccountMaxesLoading);
  const isAccountGoalsLoading = useSelector((state) => state.maxesGoals.ui.isAccountGoalsLoading);
  const isExercisesLoading = useSelector((state) => state.maxesGoals.ui.isExercisesLoading);
  const isCreatingMaxEffort = useSelector((state) => state.maxesGoals.ui.isCreatingMaxEffort);
  const isSavingGoal = useSelector((state) => state.maxesGoals.ui.isSavingGoal);
  const isDeletingGoals = useSelector((state) => state.maxesGoals.ui.isDeletingGoals);

  const [addMaxesModal, setAddMaxesModal] = useState({
    isOpen: false, athletes: [], exercise: null,
  });
  const closeAddMaxesModal = useCallback(() => {
    setAddMaxesModal({ isOpen: false, athletes: [], exercise: null });
  }, []);
  const [goalModal, setGoalModal] = useState({ isOpen: false, exerciseId: null });
  const [isExportOpen, setIsExportOpen] = useState(false);
  const tableRef = useRef(null);

  /* * Athlete View * */

  // Athletes only see their own maxes and goals, and can't edit goals here
  const isAthleteView = !currentUser.admin;

  const activeFilter = useMemo(() => (isAthleteView
    ? { ...storedFilter, filterType: USER_FILTER_TYPE.Athletes, filterIds: [currentUser.id] }
    : storedFilter
  ), [isAthleteView, storedFilter, currentUser.id]);

  const loadMaxes = useCallback((filter) => (isAthleteView
    ? dispatch(fetchMyMaxes(accountCode, currentUser.id, { exerciseId: filter.exerciseId }))
    : dispatch(fetchAccountMaxes(accountCode, filter))
  ), [isAthleteView, accountCode, currentUser.id, dispatch]);

  const loadGoals = useCallback((filter) => {
    if (isAthleteView) {
      dispatch(fetchMyGoals(accountCode));
    } else {
      dispatch(fetchAccountGoals(accountCode, {
        filterType: filter.filterType,
        filterIds: filter.filterIds,
      }));
    }
  }, [isAthleteView, accountCode, dispatch]);

  useEffect(() => {
    if (!accountCode) return;
    batch(() => {
      if (!isAthleteView) {
        dispatch(fetchGroups(accountCode));
        dispatch(fetchCalendars(accountCode));
        dispatch(fetchAthletes(accountCode));
      }
      dispatch(fetchExercises(accountCode));
      // The filter is saved so coming back from Max History keeps the same view
      if (activeFilter.filterType) loadGoals(activeFilter);
      if (shouldFetchMaxes(activeFilter)) loadMaxes(activeFilter);
    });
  }, [accountCode, dispatch]);

  /* * Filters * */

  const handleFilterChange = useCallback((filter) => {
    const nextFilter = { ...activeFilter, ...filter };
    dispatch(setMaxesGoalsFilter(nextFilter));
    if (shouldFetchMaxes(nextFilter)) loadMaxes(nextFilter);
    // Goals aren't filtered by exercise
    if (nextFilter.filterType) loadGoals(nextFilter);
  }, [activeFilter, loadMaxes, loadGoals, dispatch]);

  const handleExerciseChange = useCallback((exerciseId) => {
    const nextFilter = { ...activeFilter, exerciseId };
    dispatch(setMaxesGoalsFilter(nextFilter));
    if (shouldFetchMaxes(nextFilter)) loadMaxes(nextFilter);
  }, [activeFilter, loadMaxes, dispatch]);

  /* * Table Rows * */

  const isAthleteScope = activeFilter.filterType === USER_FILTER_TYPE.Athletes;
  const isMultiAthleteScope = activeFilter.filterType === USER_FILTER_TYPE.Groups
    || activeFilter.filterType === USER_FILTER_TYPE.Calendars
    || activeFilter.filterType === USER_FILTER_TYPE.AllAthletes;
  // Rows are athletes when groups/calendars/all athletes selected - otherwise rows are exercises
  const tableMode = isMultiAthleteScope ? 'athlete' : 'exercise';

  const athleteById = useMemo(
    () => (isAthleteView ? [currentUser] : athletes).reduce((acc, athlete) => {
      acc[athlete.id] = athlete;
      return acc;
    }, {}),
    [isAthleteView, currentUser, athletes],
  );

  const exercisesById = useMemo(
    () => exercises.reduce((acc, exercise) => {
      acc[exercise.id] = exercise;
      return acc;
    }, {}),
    [exercises],
  );

  // Empty until the view can load so old rows never show under new columns
  const rows = useMemo(() => {
    if (!shouldFetchMaxes(activeFilter)) return [];
    if (isAthleteScope) {
      return (accountMaxes[0]?.maxEfforts || []).map((effort) => ({
        ...effort,
        unitAbbr: resolveWeightUnitAbbr(effort.unitAbbr, orgWeight),
        exerciseIcon: EXERCISE_TYPE_ICON[exercisesById[effort.exerciseId]?.type] || 'lift',
        setFromLabel: getMaxSourceLabel(effort.source, exercisesById),
      }));
    }
    return accountMaxes.map((entry) => {
      const athlete = athleteById[entry.userId] || {};
      const effort = entry.maxEfforts[0] || {};
      return {
        ...effort,
        unitAbbr: resolveWeightUnitAbbr(effort.unitAbbr, orgWeight),
        userId: entry.userId,
        first: athlete.first,
        last: athlete.last,
        pic: athlete.pic,
        setFromLabel: getMaxSourceLabel(effort.source, exercisesById),
      };
    });
  }, [activeFilter, isAthleteScope, accountMaxes, athleteById, exercisesById, orgWeight]);

  // Uses the modal's exercise which can differ from the exercise picker
  const currentMaxByUserId = useMemo(() => {
    const targetExerciseId = addMaxesModal.exercise?.id;
    if (!targetExerciseId) return {};
    if (isAthleteScope) {
      const singleUserId = activeFilter.filterIds?.[0];
      const row = rows.find((r) => r.exerciseId === targetExerciseId);
      if (!singleUserId || !row) return {};
      return { [singleUserId]: { value: row.value, unitAbbr: row.unitAbbr } };
    }
    // Only valid when the picked exercise is the one being added
    if (activeFilter.exerciseId !== targetExerciseId) return {};
    return rows.reduce((acc, row) => {
      acc[row.userId] = { value: row.value, unitAbbr: row.unitAbbr };
      return acc;
    }, {});
  }, [addMaxesModal.exercise, isAthleteScope, rows, activeFilter]);

  let tableEmptyMessage = null;
  if (!activeFilter.filterType) {
    tableEmptyMessage = 'Select an athlete, group, or calendar to see maxes.';
  } else if (isMultiAthleteScope && !activeFilter.exerciseId) {
    tableEmptyMessage = 'Select an exercise to see maxes.';
  }

  /* * Titles * */

  // Names for the table caption and the goals card title
  const scopeInfo = useMemo(() => {
    const { filterType, filterIds } = activeFilter;
    if (filterType === USER_FILTER_TYPE.AllAthletes) {
      return { label: 'All Athletes', possessive: 'All Athlete' };
    }
    if (filterType === USER_FILTER_TYPE.Athletes) {
      const athlete = athleteById[filterIds?.[0]];
      return athlete ? { label: `${athlete.first} ${athlete.last}`, possessive: `${athlete.first}'s` } : null;
    }
    if (filterType === USER_FILTER_TYPE.Groups) {
      const group = groups.find((g) => g.id === filterIds?.[0]);
      return group ? { label: group.name, possessive: group.name } : null;
    }
    if (filterType === USER_FILTER_TYPE.Calendars) {
      const calendar = calendars.find((c) => c.id === filterIds?.[0]);
      return calendar ? { label: calendar.name, possessive: calendar.name } : null;
    }
    return null;
  }, [activeFilter, athleteById, groups, calendars]);

  const exerciseLabel = exercisesById[activeFilter.exerciseId]?.name;

  const tableCaption = scopeInfo && (
    `Maxes/PRs for ${scopeInfo.label}${exerciseLabel ? ` > ${exerciseLabel}` : ''}`
  );
  const goalsTitle = scopeInfo && `${scopeInfo.possessive} Goals`;
  const goalsSubtitle = activeFilter.filterType === USER_FILTER_TYPE.AllAthletes
    ? 'Account-wide goals that apply to every athlete.'
    : null;

  /* * Goals * */

  const resolveGoalSource = useCallback(
    (goal) => getGoalSourceLabel(goal, { groups, calendars, athleteById }),
    [groups, calendars, athleteById],
  );

  // Only athlete scope has one athlete's maxes to show goal progress against
  const goalProgressByExerciseId = useMemo(() => {
    if (!isAthleteScope) return {};
    return (accountMaxes[0]?.maxEfforts || []).reduce((acc, effort) => {
      if (effort.exerciseId === undefined) return acc;
      const hasPct = effort.goalPct !== undefined && effort.goalPct !== null;
      acc[effort.exerciseId] = {
        pct: hasPct ? effort.goalPct : 0,
        color: hasPct ? effort.goalColor : null,
        currentValue: effort.value,
        currentUnitAbbr: resolveWeightUnitAbbr(effort.unitAbbr, orgWeight),
      };
      return acc;
    }, {});
  }, [isAthleteScope, accountMaxes, orgWeight]);

  const goalsWithSource = useMemo(
    () => accountGoals.map((goal) => {
      const progress = goalProgressByExerciseId[goal.exerciseId];
      return {
        ...goal,
        unitAbbr: resolveWeightUnitAbbr(goal.unitAbbr, orgWeight),
        sourceLabel: goal.isInherited ? resolveGoalSource(goal) : null,
        inheritedGoalsInPriorityOrder: (goal.inheritedGoalsInPriorityOrder || [])
          .map((inheritedGoal) => ({
            ...inheritedGoal,
            unitAbbr: resolveWeightUnitAbbr(inheritedGoal.unitAbbr, orgWeight),
            sourceLabel: resolveGoalSource(inheritedGoal),
          })),
        progressPct: progress?.pct,
        progressColor: progress?.color,
        currentValue: progress?.currentValue,
        currentUnitAbbr: progress?.currentUnitAbbr,
      };
    }),
    [accountGoals, resolveGoalSource, goalProgressByExerciseId, orgWeight],
  );

  const goalScope = useMemo(() => {
    if (!scopeInfo) return null;
    const { filterType, filterIds } = activeFilter;
    const title = `Set a Goal for: ${scopeInfo.label}`;
    if (filterType === USER_FILTER_TYPE.AllAthletes) {
      return {
        appliesToType: 'Account', appliesToId: null, title, goalLabel: 'All Athletes goal',
      };
    }
    if (filterType === USER_FILTER_TYPE.Athletes) {
      return {
        appliesToType: 'Athlete', appliesToId: filterIds[0], title, goalLabel: `${scopeInfo.possessive} individual goal`,
      };
    }
    return {
      appliesToType: filterType === USER_FILTER_TYPE.Groups ? 'Group' : 'Calendar',
      appliesToId: filterIds[0],
      title,
      goalLabel: `${scopeInfo.label} goal`,
    };
  }, [scopeInfo, activeFilter]);

  const openAddGoal = useCallback(() => {
    setGoalModal({ isOpen: true, exerciseId: activeFilter.exerciseId || null });
  }, [activeFilter.exerciseId]);

  const openEditGoal = useCallback((goal) => {
    setGoalModal({ isOpen: true, exerciseId: goal.exerciseId });
  }, []);

  const closeGoalModal = useCallback(() => {
    setGoalModal({ isOpen: false, exerciseId: null });
  }, []);

  // Refetch maxes too since their progress depends on the goal
  const handleGoalChanged = useCallback(() => {
    closeGoalModal();
    loadGoals(activeFilter);
    if (shouldFetchMaxes(activeFilter)) loadMaxes(activeFilter);
  }, [activeFilter, closeGoalModal, loadGoals, loadMaxes]);

  const handleSubmitGoal = useCallback(({
    exerciseId, value, linkedExerciseId, percentage,
  }) => {
    dispatch(saveGoal(accountCode, {
      exerciseId,
      appliesToType: goalScope.appliesToType,
      appliesToId: goalScope.appliesToId,
      ...(linkedExerciseId ? { linkedExerciseId, percentage } : { value }),
    }, { onSuccess: handleGoalChanged }));
  }, [accountCode, goalScope, handleGoalChanged, dispatch]);

  const handleDeleteGoal = useCallback((goalId) => {
    dispatch(deleteGoals(accountCode, [goalId], { onSuccess: handleGoalChanged }));
  }, [accountCode, handleGoalChanged, dispatch]);

  /* * Row & Goal Card Actions * */

  const openHistory = useCallback((userId, exerciseId) => {
    if (!userId || !exerciseId) return;
    history.push(`/maxes-goals/history/${userId}/${exerciseId}`);
  }, [history]);

  const handleViewHistoryForRow = useCallback((row) => {
    if (tableMode === 'exercise') {
      openHistory(activeFilter.filterIds?.[0], row.exerciseId);
    } else {
      openHistory(row.userId, activeFilter.exerciseId);
    }
  }, [tableMode, activeFilter, openHistory]);

  const handleSelectGoalExercise = useCallback((goal) => {
    handleExerciseChange(goal.exerciseId);
  }, [handleExerciseChange]);

  const handleViewHistoryForGoal = useCallback((goal) => {
    openHistory(activeFilter.filterIds?.[0], goal.exerciseId);
  }, [activeFilter, openHistory]);

  /* * Add Maxes * */

  const handleAddMaxForRow = useCallback((row) => {
    if (tableMode === 'exercise') {
      const athlete = athleteById[activeFilter.filterIds?.[0]] || {};
      setAddMaxesModal({
        isOpen: true,
        athletes: [{
          userId: activeFilter.filterIds?.[0], first: athlete.first, last: athlete.last,
        }],
        exercise: exercisesById[row.exerciseId] || null,
      });
    } else {
      setAddMaxesModal({
        isOpen: true,
        athletes: [{ userId: row.userId, first: row.first, last: row.last }],
        exercise: exercisesById[activeFilter.exerciseId] || null,
      });
    }
  }, [tableMode, athleteById, activeFilter, exercisesById]);

  const handleAddMaxesForScope = useCallback(() => {
    const scopeAthletes = isAthleteScope
      ? [{
        userId: activeFilter.filterIds?.[0],
        first: athleteById[activeFilter.filterIds?.[0]]?.first,
        last: athleteById[activeFilter.filterIds?.[0]]?.last,
      }]
      : rows.map((row) => ({ userId: row.userId, first: row.first, last: row.last }));
    setAddMaxesModal({
      isOpen: true,
      athletes: scopeAthletes,
      exercise: exercisesById[activeFilter.exerciseId] || null,
    });
  }, [isAthleteScope, activeFilter, athleteById, rows, exercisesById]);

  const firstModalAthlete = addMaxesModal.athletes[0];
  const addMaxesModalTitle = addMaxesModal.athletes.length === 1
    ? `Add max for ${firstModalAthlete?.first} ${firstModalAthlete?.last}: ${addMaxesModal.exercise?.name}`
    : `Add maxes for ${scopeInfo?.label}: ${addMaxesModal.exercise?.name}`;

  // Health maxes always become current
  const handleSubmitAddMaxes = useCallback(({
    dateSet, setCurrentMax, isLocked, entries,
  }) => {
    const dateSetStr = format(dateSet, 'yyyy-MM-dd');
    const fieldConfig = getMaxEntryFieldConfig(addMaxesModal.exercise);
    const items = entries.map((entry) => {
      const item = {
        userId: entry.userId,
        dateSet: dateSetStr,
        setCurrentMax: fieldConfig.mode === 'health' ? true : setCurrentMax,
        lockMax: isLocked,
      };
      if (fieldConfig.mode !== 'repsOnly') item.primary1 = Number(entry.primary1 || 0);
      if (fieldConfig.mode === 'timed' || fieldConfig.mode === 'feetInches') {
        item.primary2 = Number(entry.primary2 || 0);
      }
      if (fieldConfig.mode === 'weightReps' || fieldConfig.mode === 'repsOnly') {
        item.reps = Number(entry.reps || 0);
      }
      return item;
    });
    const handleCreated = () => {
      closeAddMaxesModal();
      const changedRowIds = tableMode === 'athlete'
        ? entries.map((entry) => entry.userId)
        : [addMaxesModal.exercise.id];
      // Highlight once refreshed, since the newest max moves the row to the top
      loadMaxes(activeFilter).then(() => tableRef.current?.highlightRows(changedRowIds));
    };
    if (isAthleteView) {
      const { userId, lockMax, ...myItem } = items[0];
      dispatch(createMyMaxEffort(accountCode, addMaxesModal.exercise.id, myItem, {
        onSuccess: handleCreated,
      }));
      return;
    }
    dispatch(createMaxEfforts(accountCode, addMaxesModal.exercise.id, items, {
      onSuccess: handleCreated,
    }));
  }, [
    accountCode, activeFilter, addMaxesModal.exercise, closeAddMaxesModal, dispatch, tableMode,
    isAthleteView, loadMaxes,
  ]);

  // Manage menu
  const { manageItems, manageConfirmProps } = useManageMaxes({
    accountCode,
    activeFilter,
    rows,
    isAthleteScope,
    exercisesById,
    scopeLabel: scopeInfo?.label,
    exerciseLabel,
  });

  return (
    <Layout fullWidth>
      <Helmet>
        <title>Maxes & Goals | TeamBuildr</title>
      </Helmet>
      <PageWrapper>
        <HeaderWrapper>
          <HeaderText>Maxes & Goals</HeaderText>
        </HeaderWrapper>
        <TopRow>
          <FiltersCard>
            {isAthleteView ? (
              <Text styledTextProps={{ fontSize: '18px', fontWeight: 700, color: '#1A1A1A' }}>
                {`${currentUser.first} ${currentUser.last}`}
              </Text>
            ) : (
              <div>
                <Text
                  styledWrapperProps={{ mb: '6px' }}
                  styledTextProps={{ fontSize: '14px', fontWeight: 700, color: '#1A1A1A' }}
                >
                  Who to update
                  <br />
                  (Athletes / Group / Calendar)
                </Text>
                <MultiCategoryFilter
                  groups={groups}
                  calendars={calendars}
                  athletes={athletes}
                  value={activeFilter}
                  onChange={handleFilterChange}
                />
              </div>
            )}
            <div>
              <Text
                styledWrapperProps={{ mb: '6px' }}
                styledTextProps={{ fontSize: '14px', fontWeight: 700, color: '#1A1A1A' }}
              >
                What to update
                <br />
                (Exercises & Health)
              </Text>
              <ExercisePicker
                exercises={exercises}
                value={activeFilter.exerciseId}
                onChange={handleExerciseChange}
                isLoading={isExercisesLoading}
                allowAllTypes={isAthleteScope}
              />
            </div>
          </FiltersCard>
          <GoalsCard
            title={goalsTitle || 'Goals'}
            subtitle={goalsSubtitle}
            goals={goalsWithSource}
            isLoading={isAccountGoalsLoading}
            emptyMessage={scopeInfo ? null : 'Select an athlete, group, or calendar to see goals.'}
            onAddGoal={isAthleteView ? null : openAddGoal}
            onEditGoal={isAthleteView ? null : openEditGoal}
            showHistory={isAthleteScope}
            onViewHistory={handleViewHistoryForGoal}
            onSelectExercise={handleSelectGoalExercise}
          />
        </TopRow>
        {tableCaption && (
          <TableHeaderRow>
            <Text styledTextProps={{ fontSize: '16px', fontWeight: 700, color: '#1A1A1A' }}>
              {tableCaption}
            </Text>
            <TableHeaderActions>
              <Button
                name='Export'
                icon='download'
                iconColor='#FFF'
                customBorderRadius='4px'
                borderColor='#444444'
                txtColor='#FFF'
                bgColor='#444444'
                styledTextProps={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.3px' }}
                onClick={() => setIsExportOpen(true)}
              />
              <HeaderButton
                type='button'
                disabled={!activeFilter.exerciseId}
                onClick={handleAddMaxesForScope}
              >
                Add Maxes
              </HeaderButton>
              {!isAthleteView && <ManageMenu items={manageItems} />}
            </TableHeaderActions>
          </TableHeaderRow>
        )}
        <MaxesTable
          ref={tableRef}
          rows={rows}
          mode={tableMode}
          isLoading={isAccountMaxesLoading}
          emptyMessage={tableEmptyMessage}
          onAddMax={handleAddMaxForRow}
          onViewHistory={handleViewHistoryForRow}
        />
      </PageWrapper>
      <AddMaxesModal
        isOpen={addMaxesModal.isOpen}
        title={addMaxesModalTitle}
        athletes={addMaxesModal.athletes}
        exercise={addMaxesModal.exercise}
        currentMaxByUserId={currentMaxByUserId}
        isSubmitting={isCreatingMaxEffort}
        allowLock={!isAthleteView}
        onSubmit={handleSubmitAddMaxes}
        onClose={closeAddMaxesModal}
      />
      <SetGoalModal
        isOpen={goalModal.isOpen}
        title={goalScope?.title}
        goalLabel={goalScope?.goalLabel}
        exercises={exercises}
        goals={goalsWithSource}
        initialExerciseId={goalModal.exerciseId}
        weightUnitAbbr={resolveWeightUnitAbbr('wgt', orgWeight)}
        isSaving={isSavingGoal}
        isDeleting={isDeletingGoals}
        onSubmit={handleSubmitGoal}
        onDelete={handleDeleteGoal}
        onClose={closeGoalModal}
      />

      <ConfirmActionModal {...manageConfirmProps} />

      <ExportMaxesModal
        isOpen={isExportOpen}
        rows={rows}
        tableMode={tableMode}
        caption={tableCaption}
        scopeLabel={scopeInfo?.label}
        exerciseLabel={exerciseLabel}
        onClose={() => setIsExportOpen(false)}
      />
    </Layout>
  );
};

export default MaxesGoals;
